import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'

import { Layout } from '@percent/education-hosted-form/ui/Layout/Layout'
import { Loader } from '@percent/lemonade'
import { useEducationHostedForm } from '@percent/education-hosted-form/hooks/useEducationHostedForm/useEducationHostedForm'
import { usePartnerAnalyticsEffect } from '@percent/education-hosted-form/analytics/usePartnerAnalyticsEffect'
import { useOrganisationAnalyticsEffect } from '@percent/education-hosted-form/analytics/useOrganisationAnalyticsEffect'
import { useTrackEvent } from '@percent/education-hosted-form/analytics/useTrackEvent'
import { usePerStepINP } from '@percent/education-hosted-form/analytics/usePerStepINP'
import { EducationHostedForm } from './EducationHostedForm/EducationHostedForm'
import { useValidationSchema } from './EducationHostedForm/hooks/useValidationSchema'
import { ORGANISATION_QUESTION_METADATA } from './EducationHostedForm/hooks/useOrganisationField'
import { Organisation } from '@percent/education-hosted-form/api/organisations/organisations.types'
import { EducationHostedFormModel, EducationHostedFormSteps } from './EducationHostedForm/EducationHostedForm.types'
import { FormState } from '@percent/education-hosted-form/context/educationHostedForm/EducationHostedFormContext.types'
import { AppRoute } from '@percent/education-hosted-form/routing/AppRoute.enum'

export function EducationHostedFormContainer() {
  const { isLoading, formState, partnerConfig } = useEducationHostedForm()
  const supportedCountries = partnerConfig?.configuration.education?.supportedCountries
  const { programId } = useParams<{ programId: string }>()
  const [searchParams] = useSearchParams()
  const jwtToken = searchParams.get('token')
  const pageId = searchParams.get('pageId')
  const navigate = useNavigate()

  usePartnerAnalyticsEffect()
  const trackEvent = useTrackEvent()
  const viewedSteps = useRef<Set<EducationHostedFormSteps>>(new Set())

  const formStep = pageId ? (pageId as EducationHostedFormSteps) : EducationHostedFormSteps.SELECT_ORGANISATION

  usePerStepINP(formStep)

  const { schema } = useValidationSchema()

  const methods = useForm<EducationHostedFormModel>({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: {
      country: ''
    }
  })

  // Attribute events to the selected school (set once an org is picked from search).
  // The name comes from the stored organisation metadata so reports are readable
  // without an id lookup; both are cleared together when the org is deselected.
  const organisationId = useWatch({ control: methods.control, name: 'organisationId' })
  const selectedOrganisation = useWatch({
    control: methods.control,
    name: ORGANISATION_QUESTION_METADATA.ORGANISATION as never
  }) as Organisation | undefined
  useOrganisationAnalyticsEffect(organisationId, selectedOrganisation?.name)

  // Config loads async, so prefill the country once it arrives, but only when there's exactly one to
  // pick (same rule as the lock in SelectOrganisation). Never overwrites a selection.
  useEffect(() => {
    if (supportedCountries?.length === 1 && !methods.getValues('country')) {
      methods.setValue('country', supportedCountries[0], { shouldValidate: true })
    }
  }, [supportedCountries, methods])

  // Trigger validation every time the pageId changes to disable the next button
  useEffect(() => {
    methods.trigger()
  }, [pageId, methods])

  useEffect(() => {
    // Returns here because navigate() doesn't stop execution: without it the redirects below
    // overwrite this one and only the last destination takes effect.
    // TODO: also guard education.allowEduHostedForm === false (needs that field exposed in hosted-form-detail).
    if (!isLoading && partnerConfig && !partnerConfig.configuration.education) {
      navigate(`/${AppRoute.NOT_FOUND}`)

      return
    }

    if (formState === FormState.ERROR) {
      navigate('/')
    }

    if (programId && formState !== FormState.DIRTY) {
      navigate(`/validate/${programId}?token=${jwtToken}&pageId=${EducationHostedFormSteps.SELECT_ORGANISATION}`)
    }
  }, [pageId, programId, formState, navigate, jwtToken, isLoading, partnerConfig])

  // Emit a step-viewed event per rendered step (source for PostHog drop-off
  // funnels). SELECT_ORGANISATION always renders; any other step only renders
  // (rather than being redirected away) once the form is DIRTY. Emit at most
  // once per step so a formState transition (or back-navigation) doesn't
  // re-fire and inflate funnel counts.
  useEffect(() => {
    const isStepRendered =
      !!partnerConfig?.configuration.education &&
      (formStep === EducationHostedFormSteps.SELECT_ORGANISATION || formState === FormState.DIRTY)

    if (isStepRendered && !viewedSteps.current.has(formStep)) {
      viewedSteps.current.add(formStep)
      trackEvent('education_form_step_viewed', { step: formStep })
    }
  }, [formStep, formState, trackEvent, partnerConfig])

  if (isLoading) {
    return <Loader />
  }

  // Missing education → the redirect effect above fires; show the loader (not the unconfigured form) meanwhile
  if (partnerConfig && !partnerConfig.configuration.education) {
    return <Loader />
  }

  return (
    <Layout logo={partnerConfig?.configuration.logo}>
      <FormProvider {...methods}>
        <EducationHostedForm formStep={formStep} />
      </FormProvider>
    </Layout>
  )
}
